// @ts-ignore
import { useBlockEditContext, store as blockEditorStore } from '@wordpress/block-editor';
import { store as blocksStore } from '@wordpress/blocks';
import { useSelect, dispatch } from '@wordpress/data';
import { cloneElement } from '@wordpress/element';
import { Button } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { v4 as uuid } from 'uuid';

import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
	useSortable,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { DragHandle } from '../drag-handle';

/**
 * @typedef {object} IndexedItem
 * @property {string} id Identifier of the item
 */

/**
 * The Sortable Item Component.
 *
 * @param {object} props React props
 * @param {Function} props.children Render prop to render the children.
 * @param {object} props.item The repeater item object.
 * @param {Function|null} props.setItem A function to set state of a repeater item.
 * @param {Function|null} props.removeItem A function to delete a repeater item.
 * @param {string} props.id A string identifier for a repeater item.
 * @returns {*} React JSX
 */
const SortableItem = ({ children, item = {}, setItem = null, removeItem = null, id = '' }) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id,
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		display: 'flex',
		zIndex: isDragging ? 999 : 1,
		position: 'relative',
	};

	const repeaterItem = children(item, id, setItem, removeItem);
	const clonedRepeaterChild = cloneElement(
		repeaterItem,
		{
			ref: setNodeRef,
			style,
			className: isDragging
				? `${repeaterItem.props.className} repeater-item--is-dragging`
				: repeaterItem.props.className,
		},
		[
			<DragHandle className="repeater-item__drag-handle" {...attributes} {...listeners} isDragging={ isDragging } />,
			repeaterItem.props.children,
		],
	);

	return clonedRepeaterChild;
};

/**
 * The Repeater Component.
 *
 * @param {object} props React props
 * @param {Function} props.children Render prop to render the children.
 * @param {Function|null} props.addButton render prop to customize the "Add item" button.
 * @param {boolean} props.allowReordering boolean to toggle reordering of Repeater items.
 * @param {Function} props.onChange callback function to update the block attribute.
 * @param {Array<IndexedItem>} props.value array of Repeater items.
 * @param {Array<IndexedItem>} props.defaultValue array of default Repeater items.
 * @returns {*} React JSX
 */
export const AbstractRepeater = ({
	children,
	addButton = null,
	allowReordering = false,
	onChange,
	value,
	defaultValue = [],
}) => {
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	/**
	 * Handle drag event completion
	 *
	 * @param {import('@dnd-kit/core').DragEndEvent} event End of drag event
	 */
	function handleDragEnd(event) {
		const { active, over } = event;

		if (active.id !== over?.id) {
			/**
			 * Set of items to move.
			 *
			 * @param {Array<IndexedItem>} items Set of indexed items
			 * @returns {Array<IndexedItem>} Changed set of items
			 */
			const moveArray = (items) => {
				const oldIndex = items.findIndex((item) => item.id === active.id);
				const newIndex = items.findIndex((item) => item.id === over?.id);

				return arrayMove(items, oldIndex, newIndex);
			};

			onChange(moveArray(value));
		}
	}

	/**
	 * Adds a new repeater item.
	 */
	function addItem() {
		/*
		 * [...defaultValue] does a shallow copy. To ensure deep-copy,
		 * we do JSON.parse(JSON.stringify()).
		 */
		const defaultValueCopy = JSON.parse(JSON.stringify(defaultValue));

		if (!defaultValue.length) {
			defaultValueCopy.push({});
		}

		defaultValueCopy[0].id = uuid();

		onChange([...value, ...defaultValueCopy]);
	}

	/**
	 * Updates the item currently being edited.
	 *
	 * @param {object|string|number|boolean} newValue The value that should be used to updated the item.
	 * @param {number} index The index at which the item should be updated.
	 */
	function setItem(newValue, index) {
		/*
		 * [...value] does a shallow copy. To ensure deep-copy,
		 * we do JSON.parse(JSON.stringify()).
		 */
		const valueCopy = JSON.parse(JSON.stringify(value));

		if (typeof newValue === 'object' && newValue !== null) {
			valueCopy[index] = { ...valueCopy[index], ...newValue };
		} else {
			valueCopy[index] = newValue;
		}

		onChange(valueCopy);
	}

	/**
	 * Removes an item from the list.
	 *
	 * @param {number} index The index of the item that needs to be removed.
	 */
	function removeItem(index) {
		const valueCopy = JSON.parse(JSON.stringify(value)).filter(
			/**
			 * Filter out the item to remove.
			 *
			 * @param {IndexedItem} item Item to remove
			 * @param {number} innerIndex Current index
			 * @returns {boolean}
			 */
			// @ts-ignore
			(item, innerIndex) => index !== innerIndex,
		);
		onChange(valueCopy);
	}

	const itemIds = value.map((item) => item.id);

	return (
		<>
			{allowReordering ? (
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={(e) => handleDragEnd(e)}
					modifiers={[restrictToVerticalAxis]}
				>
					<SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
						{value.map((item, key) => {
							return (
								<SortableItem
									item={item}
									setItem={
										/**
										 * Add/update callback
										 *
										 * @param {object|string|number|boolean} val Value to set
										 * @returns {void}
										 */
										(val) => setItem(val, key)
									}
									removeItem={() => removeItem(key)}
									key={item.id}
									id={item.id}
								>
									{
										/**
										 * Mapped set of sortable items
										 *
										 * @param {IndexedItem} item Current Item
										 * @param {string} id Current Item ID
										 * @param {Function} setItem Callback to add/update an item
										 * @param {Function} removeItem Callback to remove an item
										 * @returns {*} React JSX
										 */
										(item, id, setItem, removeItem) => {
											return children(
												item,
												id,
												/**
												 * Add/update callback
												 *
												 * @param {object|string|number|boolean} val Value to set
												 * @returns {void}
												 */
												(val) => setItem(val, key),
												() => removeItem(key),
											);
										}
									}
								</SortableItem>
							);
						})}
					</SortableContext>
				</DndContext>
			) : (
				value.map((item, key) => {
					return children(
						item,
						item.id,
						/**
						 * Add/update callback
						 *
						 * @param {object|string|number|boolean} val Value to set
						 * @returns {void}
						 */
						(val) => setItem(val, key),
						() => removeItem(key),
					);
				})
			)}
			{typeof addButton === 'function' ? (
				addButton(addItem)
			) : (
				<Button variant="primary" onClick={() => addItem()}>
					{__('Add item')}
				</Button>
			)}
		</>
	);
};

/**
 * Attribute Repeater Component.
 *
 * @param {object} props React props
 * @param {Function} props.children Render prop to render the children.
 * @param {Function|null} props.addButton render prop to customize the "Add item" button.
 * @param {boolean} props.allowReordering boolean to toggle reordering of Repeater items.
 * @param {string|null} props.attribute Attribute name.
 * @returns {*} React JSX
 */
export const AttributeRepeater = ({
	children,
	attribute = null,
	addButton = null,
	allowReordering = false,
}) => {
	const { clientId, name } = useBlockEditContext();
	const { updateBlockAttributes } = dispatch(blockEditorStore);

	const attributeValue = useSelect(
		(select) => {
			// @ts-ignore
			const attributes = select(blockEditorStore).getBlockAttributes(clientId);
			return attributes[attribute] || [];
		},
		[attribute, clientId],
	);

	const { defaultRepeaterData } = useSelect(
		(select) => {
			return {
				defaultRepeaterData:
					// @ts-ignore
					select(blocksStore).getBlockType(name).attributes[attribute].default,
			};
		},
		[attribute],
	);

	if (defaultRepeaterData.length) {
		defaultRepeaterData[0].id = uuid();
	}

	/**
	 * Update a blocks attributes
	 *
	 * @param {string} value Attribute value
	 */
	const handleOnChange = (value) => {
		if (attribute !== null) {
			updateBlockAttributes(clientId, { [attribute]: value });
		}
	};

	return (
		<AbstractRepeater
			addButton={addButton}
			allowReordering={allowReordering}
			onChange={handleOnChange}
			value={attributeValue}
			defaultValue={defaultRepeaterData}
		>
			{children}
		</AbstractRepeater>
	);
};

/**
 * Repeater Component.
 *
 * @param {object} props React props
 * @param {Function} props.children Render prop to render the children.
 * @param {Function|null} props.addButton render prop to customize the "Add item" button.
 * @param {boolean} props.allowReordering boolean to toggle reordering of Repeater items.
 * @param {Function} props.onChange callback function to update the block attribute.
 * @param {Array<IndexedItem>} props.value array of Repeater items.
 * @param {Array<IndexedItem>} props.defaultValue array of default Repeater items.
 * @param {string|null} props.attribute Attribute name.
 * @returns {*} React JSX
 */
export const Repeater = ({
	children,
	addButton = null,
	allowReordering = false,
	onChange,
	value,
	defaultValue = [],
	attribute = null,
}) => {
	if (attribute) {
		return (
			<AttributeRepeater
				attribute={attribute}
				addButton={addButton}
				allowReordering={allowReordering}
			>
				{children}
			</AttributeRepeater>
		);
	}

	return (
		<AbstractRepeater
			addButton={addButton}
			allowReordering={allowReordering}
			onChange={onChange}
			value={value}
			defaultValue={defaultValue}
		>
			{children}
		</AbstractRepeater>
	);
};
